import React from 'react';
import { Paper, Typography, Box, Tooltip, IconButton } from '@mui/material';
import { Info as InfoIcon, ArrowUpward, ArrowDownward, Map as MapIcon } from '@mui/icons-material';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  description?: string;
  change?: number;
  isPercentage?: boolean;
  isPositiveGood?: boolean;
  onClick?: () => void;
  onValueClick?: () => void;
  onMapClick?: () => void;
  className?: string;
  subtitle?: string;
  ctorSubtitle?: string;
  contactCount?: number;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon,
  description,
  change,
  isPercentage = false,
  isPositiveGood = true,
  onClick,
  onValueClick,
  onMapClick,
  className = '',
  subtitle,
  ctorSubtitle,
  contactCount,
}) => {
  const formatValue = () => {
    if (typeof value === 'number') {
      if (isPercentage) {
        const normalizedValue = value > 1 ? value / 100 : value;
        return `${(normalizedValue * 100).toFixed(2)}%`;
      }
      return value.toLocaleString();
    }
    return value;
  };

  const getChangeColor = () => {
    if (change === undefined || change === 0) return 'text-gray-500';
    const isPositive = change > 0;
    const isGood = isPositiveGood ? isPositive : !isPositive;
    return isGood ? 'text-green-600' : 'text-red-600';
  };

  const getChangeText = () => {
    if (change === undefined) return null;
    
    const prefix = change > 0 ? '+' : '';
    const formattedChange = isPercentage 
      ? `${prefix}${(change * 100).toFixed(2)}%` 
      : `${prefix}${change.toLocaleString()}`;
    
    return (
      <Box className={`flex items-center ${getChangeColor()}`}>
        {change > 0 ? <ArrowUpward fontSize="small" /> : change < 0 ? <ArrowDownward fontSize="small" /> : null}
        <Typography variant="body2" className="ml-1">
          {formattedChange}
        </Typography>
      </Box>
    );
  };

  return (
    <Paper 
      elevation={0} 
      className={`p-4 h-full transition-all duration-200 border border-gray-100 ${onClick ? 'cursor-pointer hover:shadow-md' : ''} ${className}`}
      onClick={onClick}
    >
      <Box className="flex justify-between items-start mb-1">
        <Typography variant="subtitle2" className="text-gray-600 font-medium flex items-center">
          {title}
          {description && (
            <Tooltip title={description} arrow placement="top">
              <IconButton size="small" className="ml-1 text-gray-400">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Typography>
        <Box className="flex items-start gap-1">
          {onMapClick && (
            <Tooltip 
              title={
                contactCount && contactCount > 500 
                  ? "Map view is limited to 500 or fewer data points" 
                  : "View geographic map"
              } 
              arrow 
              placement="top"
            >
              <span>
                <IconButton 
                  size="small" 
                  onClick={contactCount && contactCount > 500 ? undefined : onMapClick} 
                  className={
                    contactCount && contactCount > 500 
                      ? "text-gray-300 cursor-not-allowed" 
                      : "text-blue-500 hover:text-blue-700"
                  }
                  disabled={contactCount ? contactCount > 500 : false}
                  style={{ marginTop: '-2px' }}
                >
                  <MapIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {icon && <Box className="text-gray-400" style={{ marginTop: '-2px' }}>{icon}</Box>}
        </Box>
      </Box>
      
      {/* Flexbox grid with specific row positions */}
      <Box className="flex flex-col h-full">
        {/* Row 1: Main count (fixed height) - no spacing from title */}
        <Box className="h-12 flex items-center justify-center">
          <Typography 
            variant="h4" 
            className={`font-bold text-gray-800 text-center ${onValueClick ? 'cursor-pointer underline hover:text-blue-600' : ''}`}
            onClick={onValueClick}
          >
            {formatValue()}
          </Typography>
        </Box>
        
        {/* Row 2: First percentage (fixed height) */}
        <Box className="h-6 flex items-center justify-center">
          {subtitle && (
            <Typography variant="body2" className="text-gray-500 text-center whitespace-nowrap">
              {subtitle}
            </Typography>
          )}
        </Box>
        
        {/* Row 3: Second percentage/CTOR (fixed height) */}
        <Box className="h-6 flex items-center justify-center">
          {ctorSubtitle && (
            <Typography variant="body2" className="text-gray-500 text-center whitespace-nowrap">
              {ctorSubtitle}
            </Typography>
          )}
        </Box>
        
        {/* Row 4: Flexible spacer to fill remaining space */}
        <Box className="flex-1" />
      </Box>
      
      {getChangeText()}
    </Paper>
  );
};

export default MetricCard; 